import type { ArchitectureData } from "./arch-parser";

// ---------------------------------------------------------------------------
// Canonical edge normalisation
//
// v0.3 docs put edges on flow.from / flow.to (single edge with metadata).
// v0.2 docs put adjacency on container.connects_to[] and container.actors[].
// Renderer code (use-graph-elements) reads only from this normalized list —
// every v0.2-shape read is contained here. When the last v0.2 doc gets
// re-saved, the v0.2 branches below can come out.
//
// Labels: v0.3 uses flow.what_data ?? flow.label. v0.2 looks up a step
// description matching the (source, target) pair; if no match, the edge
// renders with no label. Truncation to LABEL_MAX_LEN characters is done
// here to keep render labels short — callers receive ready-to-render
// strings.
// ---------------------------------------------------------------------------

const LABEL_MAX_LEN = 32;

export interface CanonicalEdge {
  from: string;
  to: string;
  label?: string;
  // Source data flow id when the edge came from arch.dataFlows[]. Used by
  // edge hover/click affordances to jump into the flow narrative.
  flowId?: string;
}

function shortenLabel(text: string): string {
  if (text.length <= LABEL_MAX_LEN) return text;
  return `${text.slice(0, LABEL_MAX_LEN - 1).trimEnd()}…`;
}

export function buildComponentToContainerMap(arch: ArchitectureData): Map<string, string> {
  const map = new Map<string, string>();
  for (const c of arch.containers) {
    for (const comp of c.components) {
      map.set(comp.id, c.id);
    }
  }
  // v0.3 lifts components to top level with each carrying `container`. Walk
  // both — the mapper populates the nested form for v0.2 → canonical, so this
  // is defensive.
  for (const comp of arch.components ?? []) {
    if (comp.container && !map.has(comp.id)) {
      map.set(comp.id, comp.container);
    }
  }
  return map;
}

export function findStepLabel(
  arch: ArchitectureData,
  source: string,
  target: string,
  componentToContainer: ReadonlyMap<string, string>,
): string | undefined {
  const sourceContainer = componentToContainer.get(source) ?? source;
  const targetContainer = componentToContainer.get(target) ?? target;
  for (const flow of arch.dataFlows) {
    for (const step of flow.steps ?? []) {
      const stepSourceContainer = componentToContainer.get(step.source) ?? step.source;
      const stepTargetContainer = componentToContainer.get(step.target) ?? step.target;
      if (
        (step.source === source && step.target === target) ||
        (stepSourceContainer === sourceContainer && stepTargetContainer === targetContainer)
      ) {
        return shortenLabel(step.description);
      }
    }
  }
  return undefined;
}

export function canonicalEdges(
  arch: ArchitectureData,
  componentToContainer?: ReadonlyMap<string, string>,
): CanonicalEdge[] {
  const ctm = componentToContainer ?? buildComponentToContainerMap(arch);
  const out: CanonicalEdge[] = [];
  const indexByKey = new Map<string, number>();

  function push(e: CanonicalEdge): void {
    const key = `${e.from}|${e.to}`;
    const existing = indexByKey.get(key);
    if (existing === undefined) {
      indexByKey.set(key, out.length);
      out.push(e);
      return;
    }
    // Prefer entries with a label over entries without.
    const prior = out[existing];
    if (prior && !prior.label && e.label) {
      out[existing] = e;
    }
  }

  // v0.3 edges come from each flow's choreography. Rule:
  //   - When `steps[]` is non-empty, emit one edge per step. Each step
  //     carries source/target and a per-step what_data label.
  //   - When `steps[]` is empty (rare — the v0.3 parser synthesizes a
  //     one-step entry from top-level from/to whenever both are present,
  //     so this branch only fires for malformed flows or v0.2-shape
  //     entries that came through the legacy mapper without filling
  //     either side), fall back to top-level from/to as a single edge.
  //
  // Important: NEVER emit both step edges AND the top-level from/to
  // edge. v0.2 flows authored with a 4-step choreography were upgraded
  // to v0.3 with both a "headline" from/to (the first/last hops) and a
  // populated steps[]. Emitting both double-counts the first and last
  // edges. steps[] is the source of truth when present.
  for (const f of arch.dataFlows) {
    const steps = f.steps ?? [];
    if (steps.length > 0) {
      for (const s of steps) {
        const label = s.description ? shortenLabel(s.description) : undefined;
        push({ from: s.source, to: s.target, label, flowId: f.id });
      }
    } else if (f.from && f.to) {
      const label = f.what_data
        ? shortenLabel(f.what_data)
        : f.label
          ? shortenLabel(f.label)
          : undefined;
      push({ from: f.from, to: f.to, label, flowId: f.id });
    }
  }

  // v0.2: container.connects_to[] adjacency. Label derived from flow.steps[]
  // (may be undefined — empty labels are expected for legacy docs).
  for (const c of arch.containers) {
    for (const target of c.connects_to ?? []) {
      const label = findStepLabel(arch, c.id, target, ctm);
      push({ from: c.id, to: target, label });
    }
  }

  // v0.2: container.actors[] → container (actor involvement).
  for (const c of arch.containers) {
    for (const actorId of c.actors ?? []) {
      push({ from: actorId, to: c.id });
    }
  }

  return out;
}
